// PET-642 — shared rich-clipboard helpers. Writes both a rich
// ``text/html`` payload (so clickable call-id links survive a paste into
// email / Word / Google Docs) and a ``text/plain`` fallback, degrading to
// ``clipboard.writeText`` where ``ClipboardItem`` / ``clipboard.write``
// are unavailable (non-secure contexts, older browsers).
//
// NOTE: TrainingTipsModal.tsx and trainingModuleCopy.ts still carry
// byte-identical private copies of ``writeRichClipboard`` /
// ``htmlToPlainText``. Re-pointing them at this module is a deferred
// post-wave follow-up (they are being edited concurrently in another
// session) — new copy surfaces should import from here.
// Returns ``true`` when a payload was written, ``false`` when no
// clipboard API exists at all (``navigator.clipboard`` is undefined in
// non-secure contexts) — callers can surface a "Copy failed" state
// instead of a silent no-op. Permission rejections still throw.
export async function writeRichClipboard(
  html: string,
  text: string,
): Promise<boolean> {
  const nav = navigator as Navigator & {
    clipboard?: Clipboard & {
      write?: (items: ClipboardItem[]) => Promise<void>;
    };
  };
  const CI: typeof ClipboardItem | undefined =
    typeof ClipboardItem !== "undefined" ? ClipboardItem : undefined;
  if (CI && nav.clipboard?.write) {
    const item = new CI({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([text], { type: "text/plain" }),
    });
    await nav.clipboard.write([item]);
    return true;
  }
  if (!nav.clipboard?.writeText) return false;
  await nav.clipboard.writeText(text);
  return true;
}

export function htmlToPlainText(html: string): string {
  // Browser parsing is the most accurate way to flatten the HTML we
  // just rendered. Falls back to a simple regex strip if DOMParser
  // isn't available (SSR-style environments).
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(
      `<!doctype html><body>${html}</body>`,
      "text/html",
    );
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  }
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
